import PageSeo from '../seo/PageSeo';
import { useInitialData } from '../seo/InitialData';
import React, { useMemo, useState } from 'react'
import useCatalogQuery from '../hooks/useCatalogQuery';
import { filterCollections, pageOf, sortBy, useArchive } from '../api/archive';
import CollectionBox from '../components/CollectionBox'
import FilterOptionsScroll from '../components/FilterOptionsScroll'
import Pagination from '../components/Pagination';
import {
  getActiveCatalogFilterCount,
} from './collectionUtils';

export default function Collection() {
  const initial = useInitialData();
  const { archive, error: archiveError, retry: retryArchive } = useArchive();
  const catalog = useCatalogQuery();
  const selectedKinds = catalog.kinds;
  const selectedManufacturers = catalog.manufacturers;
  const page = catalog.page;
  const pageSize = 24;
  const sortOrder = catalog.sort;
  const searchQuery = catalog.query;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = getActiveCatalogFilterCount(searchQuery, selectedKinds, selectedManufacturers);
  const sourceEntries = useMemo(() => archive?.collections || initial.entries || [], [archive, initial.entries]);
  const filterKind = useMemo(() => archive?.kinds || initial.kinds || [], [archive, initial.kinds]);
  const filterManufacturer = useMemo(() => archive?.manufacturers || initial.manufacturers || [], [archive, initial.manufacturers]);
  const filteredEntries = useMemo(() => sortBy(filterCollections(sourceEntries, { query: searchQuery, kinds: selectedKinds, manufacturers: selectedManufacturers }), 'Name', sortOrder), [sourceEntries, searchQuery, selectedKinds, selectedManufacturers, sortOrder]);
  const resultPage = useMemo(() => pageOf(filteredEntries, page, pageSize), [filteredEntries, page, pageSize]);
  const data = resultPage.entries;
  const totalPages = resultPage.totalPages;
  const totalIteams = resultPage.total;
  const isLoading = !archive && !initial.entries;
  const hasError = Boolean(archiveError && !initial.entries);

  const handleSearchChange = (event) => catalog.changeSearch(event.target.value);
  const changeSelection = (key, selected, id, checked) => {
    catalog.update({
      [key]: (checked ? [...selected, id] : selected.filter((value) => value !== id)).join(','),
      q: catalog.input.trim(), page: null,
    });
  };
  const handleKindChange = (id, checked) => changeSelection('kind', selectedKinds, id, checked);
  const handleManufacturerChange = (id, checked) => changeSelection('maker', selectedManufacturers, id, checked);
  const handleSortOrderChange = (order) => catalog.update({ sort: order === 'ASC' ? null : order, q: catalog.input.trim(), page: null });
  const clearFilters = catalog.clear;
  const pageUrl = (value) => {
    const next = new URLSearchParams(catalog.params);
    if (value === 1) next.delete('page');
    else next.set('page', value);
    return `/collection${next.size ? `?${next}` : ''}`;
  };

  return (
    <section className="retro-catalog-page">
      <PageSeo />
      <div className="retro-catalog-inner">
        <header className="retro-catalog-hero">
          <div>
            <h1>Katalog zbiorów</h1>
            <p>Znajdź komputer, konsolę lub akcesorium. Szukaj po nazwie, producencie, procesorze albo roku produkcji.</p>
          </div>

          <div className="retro-catalog-status">
            <span>{isLoading ? '…' : hasError ? '—' : totalIteams}</span>
            <p>znalezionych</p>
          </div>
        </header>

        <div className="retro-catalog-toolbar">
          <label className="retro-catalog-search">
            <i className="feather-search" aria-hidden="true"></i>
            <input
              type="search"
              value={catalog.input}
              onChange={handleSearchChange}
              placeholder="Szukaj: IBM, Amiga, monitor, Elwro..."
              aria-label="Szukaj w zbiorach"
              maxLength={160}
            />
          </label>

          <div className="retro-sort-control" aria-label="Sortowanie katalogu">
            <button
              className={sortOrder === 'ASC' ? 'active' : ''}
              type="button"
              aria-pressed={sortOrder === 'ASC'}
              onClick={() => handleSortOrderChange('ASC')}
            >
              A-Z
            </button>
            <button
              className={sortOrder === 'DESC' ? 'active' : ''}
              type="button"
              aria-pressed={sortOrder === 'DESC'}
              onClick={() => handleSortOrderChange('DESC')}
            >
              Z-A
            </button>
          </div>
        </div>

        <button className="retro-mobile-filters retro-button retro-button-secondary" type="button" aria-expanded={filtersOpen} aria-controls="catalog-filters" onClick={() => setFiltersOpen(!filtersOpen)}>
          {filtersOpen ? 'Schowaj filtry' : 'Pokaż filtry'}{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
        <div className="retro-catalog-layout">
          <aside id="catalog-filters" className={`retro-filter-panel ${filtersOpen ? 'is-open' : ''}`}>
            <div className="retro-filter-header">
              <div>
                <h2>Filtry</h2>
                <p>{activeFilterCount > 0 ? `Aktywne filtry: ${activeFilterCount}` : 'Brak aktywnych'}</p>
              </div>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters}>Wyczyść</button>
              )}
            </div>

            {archiveError && <p className="retro-filter-unavailable" role="status">Nie udało się odświeżyć pliku archiwum. Widoczne są dane z aktualnej strony.</p>}
            <div className="retro-filter-group">
              <h3>Rodzaj</h3>
              <div className="retro-filter-options">
                {filterKind.map((kind) => (
                  <label className="retro-filter-check" key={kind.id}>
                    <input
                      type="checkbox"
                      checked={selectedKinds.includes(kind.id)}
                      onChange={(event) => handleKindChange(kind.id, event.target.checked)}
                    />
                    <span>{kind.Name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="retro-filter-group">
              <h3>Producenci</h3>
              <FilterOptionsScroll>
                {filterManufacturer.map((manufacturer) => (
                  <label className="retro-filter-check" key={manufacturer.id}>
                    <input
                      type="checkbox"
                      checked={selectedManufacturers.includes(manufacturer.id)}
                      onChange={(event) => handleManufacturerChange(manufacturer.id, event.target.checked)}
                    />
                    <span>{manufacturer.Name}</span>
                  </label>
                ))}
              </FilterOptionsScroll>
            </div>
          </aside>

          <div className="retro-catalog-results" aria-busy={isLoading}>
            <div className="retro-results-header">
              <h2>Zbiory</h2>
              <p role="status" aria-live="polite">{isLoading ? 'Wyszukiwanie…' : hasError ? 'Dane niedostępne' : `Wyniki: ${totalIteams}`}</p>
            </div>

            {hasError && <div className="retro-catalog-empty" role="alert">Nie udało się pobrać pliku archiwum. <button className="retro-button retro-button-secondary" onClick={retryArchive}>Spróbuj ponownie</button></div>}

            {!hasError && !isLoading && data.length === 0 && (
              <div className="retro-catalog-empty">Brak eksponatów dla wybranych filtrów. <button className="retro-button retro-button-secondary" onClick={clearFilters}>Wyczyść filtry</button></div>
            )}

            {!hasError && !isLoading && data.length > 0 && (
              <div className="retro-catalog-grid">
                {data.map((d) => <CollectionBox key={d.id} d={d} />)}
              </div>
            )}

            <Pagination page={page} totalPages={totalPages} pageHref={pageUrl} ariaLabel="Stronicowanie zbiorów" className="retro-catalog-pagination" />
          </div>
        </div>
      </div>
    </section>
  )
}
